import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acknowledgeMarketChanges,
  getMarkets,
  getStatus,
  listRules,
  onlineStorePreferencesDeepLink,
  publishConfig,
  syncMarkets,
  toastPublishResult,
  updateSettings,
} from "../lib/safegeo-api";
import { countryLabel } from "../lib/format";
import { queryKeys } from "../lib/query-keys";
import { SafePage } from "../components/SafePage";
import { PhaseBadge, phaseFromStatus } from "../components/PhaseBadge";
import { AppLink } from "../components/AppLink";
import { AppButton } from "../components/AppButton";
import { PreviewAsVisitor } from "../components/PreviewAsVisitor";
import { ErrorBanner } from "../components/PageBanner";
import { showToast } from "../lib/feedback";
import { pickVisitorCountry } from "../lib/preview-country";

type Props = {
  /** When true, render panel only (Redirects tab) - no SafePage chrome. */
  embedded?: boolean;
};

export function MarketsPage({ embedded = false }: Props) {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [prefsChecked, setPrefsChecked] = useState(() => {
    try {
      return localStorage.getItem("sg_prefs_checked") === "1";
    } catch {
      return false;
    }
  });

  const statusQ = useQuery({ queryKey: queryKeys.status, queryFn: getStatus });
  const marketsQ = useQuery({
    queryKey: queryKeys.markets,
    queryFn: getMarkets,
  });
  const rulesQ = useQuery({ queryKey: queryKeys.rules, queryFn: listRules });

  const status = statusQ.data;
  const marketsAuto = !!status?.settings?.markets_auto;
  const marketsAdded = (
    Array.isArray(status?.settings?.markets_added)
      ? status!.settings!.markets_added
      : []
  ) as string[];
  const marketsRemoved = (
    Array.isArray(status?.settings?.markets_removed)
      ? status!.settings!.markets_removed
      : []
  ) as string[];
  const hasMarketDiff =
    marketsAdded.length > 0 || marketsRemoved.length > 0;

  const shadowByCountry = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of rulesQ.data?.items ?? []) {
      if (r.is_fallback || !r.is_enabled) continue;
      const where =
        r.action === "block"
          ? "Blocked"
          : r.destination_url
            ? `Sent to ${r.destination_url.replace(/^https?:\/\//, "").split("/")[0]} (Store redirect)`
            : "Sent via Store redirect";
      for (const c of r.countries || []) {
        map[c.toUpperCase()] = where;
      }
    }
    return map;
  }, [rulesQ.data?.items]);

  useEffect(() => {
    if (params.get("saved") !== "1") return;
    if (params.get("publish_warning")) {
      setPageError(
        "Saved, but storefront publish is still pending. Try Publish again.",
      );
      return;
    }
    showToast("Saved and live");
  }, [params]);

  // Prefer live Shopify Markets (after Refresh/sync); fall back to saved settings.
  const marketCountries =
    marketsQ.data?.countries?.length
      ? marketsQ.data.countries
      : status?.settings?.market_countries?.length
        ? status.settings.market_countries
        : [];
  const currencies = {
    ...(status?.settings?.market_currencies || {}),
    ...(marketsQ.data?.currencies || {}),
  };
  // Show Shopify Markets rows even before markets_enabled - honesty about what's configured.
  const marketRows =
    marketCountries.length > 1
      ? marketCountries.slice(1).map((c) => ({
          country: c,
          label: countryLabel(c),
          currency: currencies[c] || "",
        }))
      : [];

  const primaryCur =
    currencies[marketCountries[0]] ||
    marketsQ.data?.shop_currency ||
    "";

  const prefsUrl = status?.shop_domain
    ? onlineStorePreferencesDeepLink(status.shop_domain)
    : null;

  /** v6: Ask / Switch saves immediately + “Saved” (no SaveBar). */
  async function setModeImmediate(nextAuto: boolean) {
    if (nextAuto === marketsAuto) {
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
      return;
    }
    const countries =
      marketCountries.length >= 2
        ? marketCountries
        : marketsQ.data?.countries || [];
    setBusy(true);
    setPageError(null);
    try {
      await updateSettings({
        markets_auto: nextAuto,
        markets_enabled: countries.length >= 2,
        market_countries: countries,
        market_currencies: currencies,
      });
      let published = null;
      try {
        published = await publishConfig();
      } catch {
        /* settings saved */
      }
      const pub = toastPublishResult(
        published,
        nextAuto
          ? "Visitors will switch automatically - live on your storefront"
          : "Visitors will be asked first - live on your storefront",
      );
      if (!pub.ok && pub.message) setPageError(pub.message);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
      void qc.invalidateQueries({ queryKey: ["safegeo"] });
    } catch (e) {
      setPageError(e instanceof Error ? e.message : "Couldn’t save");
    } finally {
      setBusy(false);
    }
  }

  function markPrefsChecked() {
    try {
      localStorage.setItem("sg_prefs_checked", "1");
    } catch {
      /* ok */
    }
    setPrefsChecked(true);
    showToast("Marked as checked");
  }

  /** Re-read Shopify Markets → settings (+ publish if already live). */
  async function refreshFromShopify() {
    setBusy(true);
    setPageError(null);
    try {
      const result = await syncMarkets();
      if (result.error) {
        setPageError(`Couldn’t refresh Markets: ${result.error}`);
      } else if (!result.synced) {
        setPageError("No active Markets returned from Shopify yet");
      } else {
        const nAdd = (result.added || []).length;
        const nRem = (result.removed || []).length;
        const sample = (result.countries || [])
          .slice(0, 3)
          .map((c) => `${countryLabel(c)} ${result.currencies?.[c] || ""}`.trim())
          .join(" · ");
        if (nAdd || nRem) {
          showToast(
            `Markets updated · ${nAdd} new · ${nRem} removed${sample ? ` · ${sample}` : ""}`,
          );
        } else {
          showToast(
            result.published
              ? `Markets updated and live${sample ? `: ${sample}` : ""}`
              : `Markets updated${sample ? `: ${sample}` : ""}. Save Ask first to go live.`,
          );
        }
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.status }),
        qc.invalidateQueries({ queryKey: queryKeys.markets }),
      ]);
    } catch (e) {
      setPageError(
        e instanceof Error ? e.message : "Couldn’t refresh Markets",
      );
    } finally {
      setBusy(false);
    }
  }

  async function dismissMarketDiff() {
    setBusy(true);
    try {
      await acknowledgeMarketChanges();
      await qc.invalidateQueries({ queryKey: queryKeys.status });
      showToast("Got it");
    } catch (e) {
      setPageError(
        e instanceof Error ? e.message : "Couldn’t dismiss market changes",
      );
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <>
      
      {pageError ? (
        <ErrorBanner heading="Couldn’t update Markets">{pageError}</ErrorBanner>
      ) : null}

      {hasMarketDiff ? (
        <s-banner tone="info" heading="We found changes in Shopify">
          {marketsAdded.length
            ? `New: ${marketsAdded.map((c) => countryLabel(c)).join(", ")}. `
            : ""}
          {marketsRemoved.length
            ? `Removed: ${marketsRemoved.map((c) => countryLabel(c)).join(", ")}. `
            : ""}
          New markets default to Ask first. Removed ones stay listed until you
          dismiss.
          <s-box paddingBlockStart="small">
            <AppButton
              variant="secondary"
              disabled={busy}
              onClick={() => void dismissMarketDiff()}
            >
              Got it
            </AppButton>
          </s-box>
        </s-banner>
      ) : null}

      <s-section
        heading={
          embedded
            ? "This store (Shopify Markets)"
            : "Your markets"
        }
      >
        {marketCountries.length >= 2 ? (
          <s-stack gap="small">
            <s-paragraph color="subdued">
              Synced from Shopify Markets · updates automatically when you
              refresh. Your choice saves as soon as you pick.
            </s-paragraph>
          </s-stack>
        ) : null}
        {marketCountries.length < 2 ? (
          <s-box padding="base" borderWidth="base" borderRadius="base">
            <s-stack gap="base" alignItems="center">
              <s-text type="strong">
                You have one market, so there is nothing to switch yet
              </s-text>
              <s-paragraph color="subdued">
                SafeGeo moves visitors between markets, for example from your
                United States market to Canada with CAD prices. Add a second
                market in Shopify and it appears here.
              </s-paragraph>
              <s-stack direction="inline" gap="base">
                <AppButton
                  variant="primary"
                  href="https://admin.shopify.com/settings/markets"
                >
                  Add a market in Shopify
                </AppButton>
                <AppLink href="https://help.shopify.com/manual/international/markets">
                  Learn how markets work
                </AppLink>
              </s-stack>
              <s-paragraph color="subdued">
                Want to send visitors to another website or store instead?{" "}
                <AppLink to="/app/stores">Go to Store redirect</AppLink>.
              </s-paragraph>
            </s-stack>
          </s-box>
        ) : null}

        {marketCountries.length >= 2 && marketCountries[0] ? (
          <s-box padding="base" borderWidth="base" borderRadius="base">
            <s-stack gap="base">
              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-stack gap="small-100">
                  <s-text type="strong">
                    {countryLabel(marketCountries[0])}
                  </s-text>
                  <s-paragraph color="subdued">
                    Primary market · {primaryCur || "—"} ·{" "}
                    {status?.primary_domain ||
                      status?.shop_domain ||
                      "this store"}
                  </s-paragraph>
                </s-stack>
                <s-badge>Primary: no redirect</s-badge>
              </s-stack>

              {marketRows.map((row) => {
                const sameCurrency =
                  !!row.currency &&
                  !!primaryCur &&
                  row.currency === primaryCur;
                const shadow = shadowByCountry[row.country.toUpperCase()];
                const cc = row.country.toUpperCase();
                const isNew = marketsAdded.some(
                  (c) => String(c).toUpperCase() === cc,
                );
                const cur = row.currency || "";
                return (
                  <s-stack key={`m-${row.country}`} gap="base">
                    <s-divider />
                    <s-stack
                      direction="inline"
                      gap="base"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <s-stack gap="small-100">
                        <s-stack
                          direction="inline"
                          gap="small"
                          alignItems="center"
                        >
                          <s-text type="strong">{row.label}</s-text>
                          {isNew ? <s-badge tone="info">New</s-badge> : null}
                        </s-stack>
                        <s-paragraph color="subdued">
                          {shadow
                            ? shadow
                            : `${cur || "—"} · this store`}
                        </s-paragraph>
                      </s-stack>
                      {shadow ? (
                        <s-badge tone="info">Overridden</s-badge>
                      ) : (
                        <s-stack
                          direction="inline"
                          gap="small"
                          alignItems="center"
                        >
                          <s-button
                            type="button"
                            variant={!marketsAuto ? "primary" : "secondary"}
                            disabled={busy || sameCurrency}
                            onClick={() => void setModeImmediate(false)}
                          >
                            Ask first
                          </s-button>
                          <s-button
                            type="button"
                            variant={marketsAuto ? "primary" : "secondary"}
                            disabled={busy || sameCurrency}
                            onClick={() => void setModeImmediate(true)}
                          >
                            Switch automatically
                          </s-button>
                          {savedFlash ? (
                            <s-badge tone="success">Saved</s-badge>
                          ) : null}
                        </s-stack>
                      )}
                    </s-stack>
                  </s-stack>
                );
              })}

              {marketsRemoved.map((cc) => (
                <s-stack key={`rm-${cc}`} gap="base">
                  <s-divider />
                  <s-stack gap="small-100">
                    <s-stack direction="inline" gap="small" alignItems="center">
                      <s-text type="strong">{countryLabel(cc)}</s-text>
                      <s-badge tone="warning">Removed in Shopify</s-badge>
                    </s-stack>
                    <s-paragraph color="subdued">
                      Setting paused and kept. Its countries now follow your
                      other rules.
                    </s-paragraph>
                  </s-stack>
                </s-stack>
              ))}
            </s-stack>
          </s-box>
        ) : marketCountries.length < 2 ? null : (
          <s-box paddingBlockStart="base">
            <s-paragraph color="subdued">
              Add a second market in Shopify, then tap Refresh.
            </s-paragraph>
          </s-box>
        )}
        {marketCountries.length >= 2 ? (
          <s-box paddingBlockStart="base">
            <s-banner tone="info">
              <s-text type="strong">Ask first</s-text> shows a popup and the
              visitor chooses.{" "}
              <s-text type="strong">Switch automatically</s-text> changes
              currency and language with no popup, and counts toward your
              monthly limit. EU visitors are always asked first (change in
              Settings, Protection).
            </s-banner>
          </s-box>
        ) : null}
        <s-box paddingBlockStart="base">
          <s-stack direction="inline" gap="base" alignItems="center">
            <AppLink href="https://admin.shopify.com/settings/markets">
              Edit markets in Shopify
            </AppLink>
            <AppLink to="/app/popup">Change how the popup looks</AppLink>
          </s-stack>
        </s-box>
      </s-section>

      {!embedded ? (
        <PreviewAsVisitor prefer={marketCountries} context="market" />
      ) : (
        <s-section heading="Shopify Automatic redirection">
          <s-stack direction="inline" gap="base" alignItems="center">
            <s-badge tone={prefsChecked ? "success" : "info"}>
              {prefsChecked ? "Reviewed" : "Not verified"}
            </s-badge>
            <s-button
              type="button"
              variant="tertiary"
              commandFor="how-check-markets"
              command="--show"
            >
              How to check
            </s-button>
            {!prefsChecked ? (
              <s-button
                type="button"
                variant="secondary"
                onClick={markPrefsChecked}
              >
                Mark as reviewed
              </s-button>
            ) : null}
          </s-stack>
        </s-section>
      )}

      <s-modal
        id="how-check-markets"
        heading="Check Shopify’s Automatic redirection"
      >
        <s-ordered-list>
          <s-list-item>
            Open{" "}
            {prefsUrl ? (
              <AppLink href={prefsUrl}>Online Store › Preferences</AppLink>
            ) : (
              <s-text type="strong">Online Store › Preferences</s-text>
            )}
            .
          </s-list-item>
          <s-list-item>
            Find <s-text type="strong">Automatic redirection</s-text>.
          </s-list-item>
          <s-list-item>If it’s on, turn it off and save.</s-list-item>
        </s-ordered-list>
        <s-paragraph color="subdued">
          A visitor from{" "}
          {countryLabel(pickVisitorCountry(marketCountries, "CA"))} then sees
          the SafeGeo popup.
        </s-paragraph>
        <AppButton
          slot="primary-action"
          commandFor="how-check-markets"
          command="--hide"
          onClick={markPrefsChecked}
        >
          Mark as reviewed
        </AppButton>
      </s-modal>
    </>
  );

  if (embedded) return body;

  const phase = phaseFromStatus({
    published: !!status?.last_published_at,
    paused: !!status?.settings?.paused,
  });

  return (
    <SafePage
      screen="markets"
      heading="Market redirect"
      accessory={<PhaseBadge phase={phase} />}
      secondaryActions={
        <AppButton
          slot="secondary-actions"
          disabled={busy}
          onClick={() => void refreshFromShopify()}
        >
          Refresh from Shopify
        </AppButton>
      }
    >
      {body}
    </SafePage>
  );
}
